import { Component, OnInit } from '@angular/core';
import { AbstractControl, FormBuilder, FormGroup, ValidationErrors, Validators } from '@angular/forms';

import { Company } from 'app/models/company.models';
import { ResponsibleType } from 'app/models/responsible-type.models';
import { UploadImage } from 'app/interfaces/upload-images.interface';
import { ArchivosService } from 'app/services/archivos.service';
import { EmpresaService } from 'app/services/empresa.service';
import { ParametrosService } from 'app/services/parametros.service';
import { UtilService } from 'app/shared/utils.service';
import Swal from 'sweetalert2';
import { DataService } from 'app/services/data.service';

// Custom validator to prevent future dates
export function noFutureDateValidator(control: AbstractControl): ValidationErrors | null {
  if (!control.value) {
    return null; // Don't validate if there's no value
  }
  const today = new Date();
  today.setHours(0, 0, 0, 0);

  // The value from an <input type="date"> is a string 'YYYY-MM-DD'.
  // To avoid timezone issues, we construct the date from parts.
  const [year, month, day] = control.value.split('-').map(Number);
  const controlDate = new Date(year, month - 1, day);

  if (controlDate > today) {
    return { futureDate: true };
  }
  return null;
}

@Component({
  selector: 'app-empresa',
  templateUrl: './empresa.component.html',
  styleUrls: ['./empresa.component.css'],
})
export class EmpresaComponent implements OnInit {
  constructor(
    private fb: FormBuilder,
    private empresaService: EmpresaService,
    private archivosService: ArchivosService,
    private utilService: UtilService,
    private parametroServicio: ParametrosService,
    private dataService: DataService
  ) {
    this.myForm = this.fb.group({
      documentNumber: ['', [Validators.required, Validators.minLength(12)]],
      socialReason: ['', [Validators.required, this.utilService.stringMaxLength]],
      fantasyName: ['', [Validators.required, this.utilService.stringMaxLength]],
      commercialAddress: ['', [Validators.required]],
      // inicio de actividad como máximo fecha actual
      activityStartDate: ['', [Validators.required, noFutureDateValidator]],
      phone: ['', [Validators.required, Validators.minLength(10), Validators.maxLength(13)]],
      email: [
        '',
        [
          Validators.required,
          Validators.pattern('^[a-z0-9._%+-]+@[a-z0-9.-]+\\.[a-z]{2,4}$'),
          Validators.maxLength(255),
        ],
      ],
      logo: [''],
      responsibleTypeId: ['', [Validators.required]],
      grossIncome: ['', [Validators.required]],
    });
    this.myForm.controls['email'].setValidators(this.utilService.validarEmails);

    this.autoDisabledForm();
  }
  public empConfig?: Company;
  public myForm: FormGroup;
  public imgReaderUpload?: string | ArrayBuffer;
  public actualizarImg: boolean = false;
  responsables: ResponsibleType[] = [];
  public inputHasChange: boolean = true;
  idEmpresa: number;
  private imgToUpload: File;
  textoBtnSubmit = 'Guardar';
  public readonly today = new Date().toISOString().split('T')[0];
  logoDefault = 'assets/img/logo.jpg';
  isLoading: boolean = false;

  ngOnInit(): void {
    this.empresaService.getEmpresaConfig().subscribe(data => {
      this.empConfig = data;
      if (this.empConfig) {
        this.idEmpresa = this.empConfig.id;
      }
      this.myForm.patchValue(this.empConfig);
      if (this.empConfig.logo === '') {
        this.empConfig.logo = this.logoDefault;
      }
    });
    this.getParametros();
  }

  getParametros() {
    this.parametroServicio.getParametrosClientes().subscribe(data => {
      this.responsables = data.tiporesponsables;
      if (this.empConfig) {
        this.myForm.controls.responsibleTypeId.setValue(this.empConfig.responsibleTypeId, {
          onlySelf: true,
        });
      }
    });
  }

  autoDisabledForm() {
    this.myForm.controls['documentNumber'].disable();
    this.myForm.controls['socialReason'].disable();
    this.myForm.controls['fantasyName'].disable();
    this.myForm.controls['commercialAddress'].disable();
    this.myForm.controls['phone'].disable();
    this.myForm.controls['email'].disable();
    this.myForm.controls['responsibleTypeId'].disable();
    this.myForm.controls['activityStartDate'].disable();
    this.myForm.controls['grossIncome'].disable();
    this.actualizarImg = false;
  }

  changeDisabled(field: string): void {
    const inputControl = this.myForm.get(field);
    inputControl.disabled ? inputControl.enable() : inputControl.disable();
  }

  changeTemplate(text: string) {
    if (!this.actualizarImg) {
      this.actualizarImg = true;
    } else {
      this.actualizarImg = false;
    }
    if (text === 'close' && this.imgReaderUpload !== undefined) {
      this.empConfig.logo = this.imgReaderUpload as string;
    }
  }

  async preImg(event) {
    if (event.target.files.length === 0) return;
    const imgSelected = event.target.files[0];

    if (imgSelected && imgSelected.type.indexOf('image') === -1) {
      Swal.fire({
        title: 'Error!',
        text: 'Se admiten archivos con extensión del type .jpg/.jpeg/.png',
        icon: 'warning',
        confirmButtonText: 'Salir',
      });
      return false;
    }

    const reader = new FileReader();

    reader.onload = e => {
      this.imgReaderUpload = this.empConfig.logo;
      this.empConfig.logo = e.target.result as string;
      this.imgToUpload = imgSelected;
    };
    reader.readAsDataURL(imgSelected);
  }

  onChangeInput(field: string, event?) {
    let inputValue = this.myForm.get(field).value;

    if (event && event.target.files[0]) {
      inputValue = event.target.files[0].name;
    }

    inputValue !== this.empConfig[field] ? (this.inputHasChange = false) : (this.inputHasChange = true);
  }

  checkWord({ keyCode }: KeyboardEvent) {
    return this.utilService.validarCaracteres(keyCode);
  }

  isValidField(field: string): boolean | null {
    return this.myForm.controls[field].errors && this.myForm.controls[field].touched;
  }

  async uploadFile() {
    if (!this.imgToUpload) return null;
    try {
      const res: UploadImage = await this.archivosService.subirImagen(this.imgToUpload);
      // La respuesta puede tener la URL en diferentes ubicaciones dependiendo del formato
      if (res) {
        const logoString = res.url || res.data?.url;
        sessionStorage.setItem('empresalogo', logoString);
        this.dataService.getDatos();
      }

      return res.url || res.data?.url;
    } catch (error) {
      Swal.fire({
        title: 'Error!',
        text: 'Hubo un inconveniente al querer actualizar su logo',
        icon: 'error',
        confirmButtonText: 'Salir',
      });
      return null;
    }
  }
  async guardar() {
    if (this.isLoading) return; // Prevenir múltiples clicks

    this.isLoading = true;
    this.textoBtnSubmit = 'Guardando...';

    try {
      const url = await this.uploadFile();
      this.empConfig = this.myForm.getRawValue();
      this.empConfig.phone = String(this.empConfig.phone ?? '');
      if (this.idEmpresa) {
        if (url) {
          this.empConfig.logo = url;
        }
        this.empConfig.id = this.idEmpresa;
        this.empresaService.actualizarDataEmpresa(this.empConfig).subscribe(
          data => {
            this.isLoading = false;
            this.textoBtnSubmit = 'Guardar';
            this.autoDisabledForm();
            Swal.fire({
              title: 'Excelente!',
              text: `Sus datos se han actualizado correctamente!`,
              confirmButtonColor: '#3085d6',
              timer: 5000,
            });
            this.imgReaderUpload = this.empConfig.logo;
            this.inputHasChange = true;
          },
          err => {
            this.isLoading = false;
            this.textoBtnSubmit = 'Guardar';
            Swal.fire({
              title: 'Error!',
              text: 'Hubo un inconveniente al modificar los datos de la empresa',
              icon: 'error',
              confirmButtonText: 'Salir',
            });
          }
        );
      } else {
        // guardamos
        if (url) {
          this.empConfig.logo = url;
        }

        this.empresaService.guardarEmpresa(this.empConfig).subscribe(
          data => {
            this.isLoading = false;
            this.textoBtnSubmit = 'Guardar';
            this.autoDisabledForm();
            Swal.fire({
              title: 'Excelente!',
              text: `Sus datos se han guardado correctamente!`,
              confirmButtonColor: '#3085d6',
              timer: 5000,
            });
            this.imgReaderUpload = this.empConfig.logo;
            this.inputHasChange = true;
          },
          err => {
            this.isLoading = false;
            this.textoBtnSubmit = 'Guardar';
            Swal.fire({
              title: 'Error!',
              text: 'Hubo un inconveniente al guardar los datos de la empresa',
              icon: 'error',
              confirmButtonText: 'Salir',
            });
          }
        );
      }
    } catch (error) {
      this.isLoading = false;
      this.textoBtnSubmit = 'Guardar';
      console.log({ error });
    }
  }
}
